import { isNot } from '@ipfabric/utils/fp/comparison';
import { addPropMutable } from '@ipfabric/utils/fp/prop';
import { MUTED_ELEMENT_ALPHA } from 'components/Graph/GraphApp/colors';
import type {
  EdgeReasonToHide,
  NodeReasonToHide,
} from 'components/Graph/GraphApp/hideReasons';
import type { GraphRendererPluginInterface } from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import { set } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { Container } from 'pixi.js';
import {
  type CompositeHiding,
  installCompositeHiding,
} from 'utils/pixi/compositeHiding';

type GraphDisplayObjectType = 'node' | 'edge';

export type GraphDisplayObject<
  TGraphDisplayObjectType extends
    GraphDisplayObjectType = GraphDisplayObjectType,
  TReasonToHide extends string = string,
> = Container &
  CompositeHiding<TReasonToHide> & {
    isHiddenFromBounds: () => boolean;
    graphDisplayObjectType: TGraphDisplayObjectType;
    updatePeekingAtHiddenNodes: () => void;
    /** Display the element semi-opaque when search is active and this element is not in results */
    setMutedBySearch: (muted: boolean) => void;
  };

type GraphDisplayObjectPrivate = {
  mutedBySearch?: boolean;
};

const hasHideFromBoundsReason = set.some(isNot('CULLED'));

export const createGraphDisplayObject = <
  TGraphDisplayObjectType extends GraphDisplayObjectType,
  TReasonToHide extends NodeReasonToHide | EdgeReasonToHide,
>(
  pluginInterface: GraphRendererPluginInterface,
  type: TGraphDisplayObjectType,
  onVisibilityToggledByPeeking: () => void,
  userHiddenReasonsToHide: TReasonToHide[],
): GraphDisplayObject<TGraphDisplayObjectType, TReasonToHide> => {
  const isMutedByPeeking = () =>
    pluginInterface.isPeekingAtHiddenNodes() && element.reasonsToHide.size > 0;
  const isMuted = () => isMutedByPeeking() || element.mutedBySearch;
  const updateMuted = () => {
    element.alpha = isMuted() ? MUTED_ELEMENT_ALPHA : 1;
  };

  const isOnlyUserHidden = set.every((reason: TReasonToHide) =>
    userHiddenReasonsToHide.includes(reason),
  );

  const isVisible = (reasonsToHide: Set<TReasonToHide>) =>
    reasonsToHide.size === 0 ||
    (pluginInterface.isPeekingAtHiddenNodes() &&
      isOnlyUserHidden(reasonsToHide));

  const element: GraphDisplayObject<TGraphDisplayObjectType, TReasonToHide> &
    GraphDisplayObjectPrivate = pipe(
    installCompositeHiding(new Container(), isVisible, updateMuted),
    addPropMutable('isHiddenFromBounds', () =>
      pluginInterface.isPeekingAtHiddenNodes()
        ? false
        : hasHideFromBoundsReason(element.reasonsToHide),
    ),
    addPropMutable('setMutedBySearch', (muted: boolean) => {
      element.mutedBySearch = muted;
      updateMuted();
    }),
    addPropMutable('graphDisplayObjectType', type),
    addPropMutable('updatePeekingAtHiddenNodes', () => {
      updateMuted();
      if (hasHideFromBoundsReason(element.reasonsToHide)) {
        onVisibilityToggledByPeeking();
      }
    }),
  );
  return element;
};
